import { Component, EventEmitter, Input, Output, OnInit, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormGroup, FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { Abstract } from '../../../../models/abstract/abstract.model';
import { AbstractText } from '../../../../models/abstract/abstract-text.model';

import { AbstractService } from '../../../../services/abstract.service';
import { AlertService, MessageSeverity } from '../../../../services/alert.service';
import { Utilities } from '../../../../services/utilities';
import { NgClass } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';


const formTitleKey = {
  userId: "User ID",
  date: "Date",
  projectTitle: "Project Title",
  administeringIC: "Administering IC",
  applicationID: "Application ID",
  piProjectLeader: "PI Project Leader",
  fy: "FY",
  projectNumber: "Project Number",
  description: "Description",
  publicHealthRel: "Public Health Relevance"
};

@Component({
  selector: 'app-abstract-form',
  templateUrl: './abstract-form.component.html',
  styleUrls: ['../abstract-container.component.css', './abstract-form.component.css'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    NgClass,
    ReactiveFormsModule
  ]
})
export class AbstractFormComponent implements OnInit {
  @Output() cancel = new EventEmitter<void>();
  @Output() updated = new EventEmitter<Abstract>();
  @Input() abstract: Abstract;
  @Input() userName: string;

  abstractForm: FormGroup;

  constructor(private abstractService: AbstractService, private alertService: AlertService, private router: Router) { }

  ngOnInit() {
    this.initializeForm();
  }

  initializeForm() {
    if (this.abstract) {
      this.abstractForm = new FormGroup({
        date: new FormControl(this.abstract.lastModifiedDate, Validators.required),
        projectTitle: new FormControl(this.abstract.projectTitle , Validators.required),
        administeringIC: new FormControl(this.abstract.administeringIC, Validators.required),
        applicationID: new FormControl(this.abstract.application, Validators.required),
        piProjectLeader: new FormControl(this.abstract.piProjectLeader, Validators.required),
        fy: new FormControl(this.abstract.fy, Validators.required),
        projectNumber: new FormControl(this.abstract.projectNumber, Validators.required),
        description: new FormControl(this.abstract.abstractText.descPart, Validators.required),
        publicHealthRel: new FormControl(this.abstract.abstractText.publicHealthPart, Validators.required)
      });
    }
    else {
      this.abstractForm = new FormGroup({
        date: new FormControl('', Validators.required),
        projectTitle: new FormControl('', Validators.required),
        administeringIC: new FormControl('', Validators.required),
        applicationID: new FormControl('', Validators.required),
        piProjectLeader: new FormControl('', Validators.required),
        fy: new FormControl('', Validators.required),
        projectNumber: new FormControl('', Validators.required),
        description: new FormControl('', Validators.required),
        publicHealthRel: new FormControl('', Validators.required)
      });
    }
  }

  handleCancel() {
    this.cancel.emit();
  }
  
  handleSubmit() {
    if (!this.abstractForm.valid) {
      this.alertService.showMessage("Abstract Form is Invalid", 
        this.formatValidationError(this.abstractForm), MessageSeverity.error);
      return;
    }

    const newAbstract = this.mapFormGroupToAbstract();

    if (this.abstract) {
      this.abstractService.updateAbstract(newAbstract).subscribe({
        next: abstract => {
        this.alertService.showMessage("Success", "The abstract has been updated!", MessageSeverity.success);
        this.updated.emit(abstract);
      },
      error: (error) => {
        this.alertService.showStickyMessage("Server Error", 
          `Unable to update abstract.\r\nErrors: "${Utilities.getHttpResponseMessage(error)}"`,
          MessageSeverity.error, error);
      }});
    }
    else {
      this.abstractService.addAbstract(newAbstract).subscribe({
        next: abstract => {
        this.router.navigateByUrl(`/abstract/view/${abstract.id}`);
        this.alertService.showMessage("Success", "A new abstract has been added!", MessageSeverity.success);
      },
      error: (error) => {
        this.alertService.showStickyMessage("Server Error", 
          `Unable to create new abstract.\r\nErrors: "${Utilities.getHttpResponseMessage(error)}"`,
          MessageSeverity.error, error);
      }});
    }
  }

  mapFormGroupToAbstract(): Abstract {
    const formControls = this.abstractForm.controls;
    
    // TODO: Come up with better mapping between formgroup and object.
    return new Abstract(
      null,
      this.userName,
      formControls['date'].value,
      new AbstractText(
        formControls['description'].value,
        formControls['publicHealthRel'].value
      ),
      formControls['administeringIC'].value,
      formControls['applicationID'].value, 
      new Date(),
      formControls['fy'].value,
      formControls['piProjectLeader'].value,
      formControls['projectTitle'].value,
      formControls['projectNumber'].value);
  }

  formatValidationError(formGroup: FormGroup): string {
    const formatEnd = (arr: any[], i: number) => {
      return i != arr.length - 1 ? ", " : ` field${arr.length == 1 ? " is" : "s are"} required.`;
    }
    return Object.entries(formGroup.controls)
      .filter(control => control[1].errors && control[1].errors.required)
      .reduce((message, control, i, arr) => `${message}${formTitleKey[control[0]]}${formatEnd(arr, i)}`, "")
  }
}
